import { EMAIL_NOTIFICATION, NOTIFICATION_FEATURE_LABEL } from './constants';

const ROLE_LABEL = (EMAIL_NOTIFICATION.roles || []).reduce((map, role) => {
  map[role.value] = role.name;
  return map;
}, {});

export function shortTimezoneAbbr() {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    const tz = parts.find((p) => p.type === 'timeZoneName');
    return tz?.value || 'local';
  } catch {
    return 'local';
  }
}

/** Run history time column — e.g. "13 Oct 2026, 09:00" */
export function formatActivityRunAt(isoUtc) {
  if (!isoUtc) return '';
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}, ${time}`;
}

export function activityCycleLabel(item) {
  const cycles = item?.cycles || [];
  if (!cycles.length) return '';
  if (cycles.length === 1) return cycles[0].name || '';
  return `${cycles.length} cycles`;
}

export function activityEmailsLabel(item) {
  const count = Number(item?.emails) || 0;
  if (count === 0) return 'Nobody matched';
  return count === 1 ? '1 email' : `${count} emails`;
}

export function activityOutcomeChip(item) {
  const emails = Number(item?.emails) || 0;
  const failed = Number(item?.failed) || 0;
  const sent = Math.max(0, emails - failed);

  if (emails === 0 && failed === 0) {
    return { text: 'No recipients', variant: 'neutral' };
  }
  if (failed > 0 && sent > 0) {
    return { text: `${sent} sent · ${failed} failed`, variant: 'warn' };
  }
  if (failed > 0) {
    return { text: `${failed} failed`, variant: 'error' };
  }
  const total = emails || sent;
  return { text: `${total} sent`, variant: 'success' };
}

export function activitySourceLabel(itemOrSource) {
  if (itemOrSource && typeof itemOrSource === 'object') {
    const row = itemOrSource;
    if (row.source) return row.source;
    if (row.feature && NOTIFICATION_FEATURE_LABEL[row.feature]) {
      return NOTIFICATION_FEATURE_LABEL[row.feature];
    }
    return '';
  }
  if (itemOrSource === 'template') return 'Template';
  if (itemOrSource === 'notification') return 'Notification';
  return itemOrSource || '';
}

export function activityRecipientRoleLabel(role) {
  if (!role) return '';
  if (ROLE_LABEL[role]) return ROLE_LABEL[role];
  const text = String(role).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function activityRecipientLine(child) {
  const recipient = child?.recipient || {};
  const name = recipient.name || recipient.emailMasked || '—';
  const role = activityRecipientRoleLabel(recipient.role);
  const email = recipient.emailMasked || '';
  const parts = [name];
  if (role) parts.push(role);
  if (email) parts.push(email);
  return parts.join(' · ');
}

export function activityChildCyclesLabel(child) {
  if (child?.cycles_label) return child.cycles_label;
  const cycles = child?.cycles;
  if (Array.isArray(cycles) && cycles.length) {
    const names = cycles.map((cycle) => cycle?.name).filter(Boolean);
    if (names.length) return names.join(', ');
  }
  return '—';
}

export function activityChildStatusChip(status) {
  const value = status && typeof status === 'object' ? status.status : status;
  if (value === 'success') return { text: 'Sent', variant: 'success', icon: 'mdi-check' };
  if (value === 'failed') return { text: 'Failed', variant: 'error', icon: 'mdi-alert-circle-outline' };
  if (value === 'queued') return { text: 'Queued', variant: 'info', icon: 'mdi-clock-outline' };
  const label = value ? String(value) : '—';
  return { text: label, variant: 'neutral', icon: null };
}
